import { useEffect } from 'react';
import { toast } from 'sonner';

// Build timestamp is embedded at build time
const BUILD_VERSION = import.meta.env.VITE_BUILD_TIME || Date.now().toString();
const VERSION_KEY = 'settleup_app_version';
const API_CACHE_KEY = 'supabase-api-cache';

/**
 * Hook to detect app version changes and handle cache invalidation.
 * When a new version is detected, it clears stale caches and optionally reloads.
 */
export const useAppVersion = () => {
  useEffect(() => {
    const storedVersion = localStorage.getItem(VERSION_KEY);
    
    if (storedVersion && storedVersion !== BUILD_VERSION) {
      console.log(`🔄 App version changed: ${storedVersion} → ${BUILD_VERSION}`);
      
      // Clear the Supabase API cache to get fresh data
      if ('caches' in window) {
        caches.delete(API_CACHE_KEY).then((deleted) => {
          if (deleted) {
            console.log('✅ Cleared stale API cache');
          }
        });
      }
      
      // Show a brief toast to indicate fresh data
      toast.success('App updated!', {
        description: 'You now have the latest version.',
        duration: 2000,
      });
    }
    
    // Store the current version
    localStorage.setItem(VERSION_KEY, BUILD_VERSION);
  }, []);
};

/**
 * Manually trigger a cache clear and reload.
 * Useful for debugging or forcing a fresh start.
 */
export const forceAppRefresh = async () => {
  console.log('🔄 Force refreshing app...');
  
  // Clear all relevant caches
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(name => {
        console.log(`🧹 Clearing cache: ${name}`);
        return caches.delete(name);
      })
    );
  }
  
  // Clear version to trigger fresh detection
  localStorage.removeItem(VERSION_KEY);
  
  // Unregister service worker and reload
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map(r => r.unregister()));
  }
  
  // Hard reload
  window.location.reload();
};
